import apiService from '@/services/api';
import router from '@/router';

const TOKEN_KEY = 'userToken';
const USER_KEY = 'userLoggedIn';
const USERNAME_KEY = 'username';
const PERMISSIONS_KEY = 'botPermissions';
const USER_DATA_KEY = 'userData';

// Debug initial state loading
const storedUserData = localStorage.getItem(USER_DATA_KEY);
console.log('Auth module - Loading initial state');
console.log('Auth module - USER_DATA_KEY:', USER_DATA_KEY);
console.log('Auth module - Stored user data:', storedUserData);
console.log('Auth module - Parsed user:', storedUserData ? JSON.parse(storedUserData) : null);

export default {
  namespaced: true,
  
  state: {
    user: storedUserData ? JSON.parse(storedUserData) : null,
    token: localStorage.getItem(TOKEN_KEY) || null,
    isAuthenticated: localStorage.getItem(USER_KEY) === 'true',
    botPermissions: JSON.parse(localStorage.getItem(PERMISSIONS_KEY) || '[]'),
    isLoading: false,
    error: null,
  },
  
  mutations: {
    SET_USER(state, user) {
      console.log('SET_USER mutation called with:', user);
      state.user = user;
      if (user) {
        localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
        console.log('Saved to localStorage:', localStorage.getItem(USER_DATA_KEY));
      } else {
        localStorage.removeItem(USER_DATA_KEY);
      }
    },
    
    SET_TOKEN(state, token) {
      state.token = token;
      if (token) {
        localStorage.setItem(TOKEN_KEY, token);
        // apiService uses fetch, not axios, so no need to set default headers
      } else {
        localStorage.removeItem(TOKEN_KEY);
        // No need to delete headers since apiService builds them on each request
      }
    },
    
    SET_AUTHENTICATED(state, isAuthenticated) {
      state.isAuthenticated = isAuthenticated;
      localStorage.setItem(USER_KEY, isAuthenticated);
    },
    
    SET_BOT_PERMISSIONS(state, permissions) {
      state.botPermissions = permissions;
      localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
    },
    
    SET_LOADING(state, isLoading) {
      state.isLoading = isLoading;
    },
    
    SET_ERROR(state, error) {
      state.error = error;
    },
    
    CLEAR_AUTH(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.botPermissions = [];
      state.error = null;

      // Clear localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(USERNAME_KEY);
      localStorage.removeItem(PERMISSIONS_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      localStorage.removeItem('userMaintenance'); // Clear maintenance flag

      // No need to clear auth header since apiService builds headers on each request
    },

    LOGOUT(state) {
      // Use CLEAR_AUTH para limpar tudo
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.botPermissions = [];
      state.error = null;

      // Clear localStorage
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(USERNAME_KEY);
      localStorage.removeItem(PERMISSIONS_KEY);
      localStorage.removeItem(USER_DATA_KEY);
      localStorage.removeItem('userMaintenance'); // Clear maintenance flag
    },
  },
  
  actions: {
    async login({ commit, dispatch }, credentials) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      try {
        const response = await apiService.post('/login/', credentials);
        
        // apiService returns data directly, not response.data
        console.log('Login - Full response:', response);
        const { token, user, bot_permissions } = response;
        
        if (!token) {
          throw new Error('No token received from server');
        }
        
        // Set auth data
        console.log('Login response - user data:', user);
        console.log('Login response - is_superuser:', user?.is_superuser);
        console.log('Login - About to commit SET_USER with:', user);
        
        commit('SET_TOKEN', token);
        commit('SET_USER', user || { username: credentials.username });
        commit('SET_AUTHENTICATED', true);
        commit('SET_BOT_PERMISSIONS', bot_permissions || []);
        
        // Verify what was saved in localStorage
        console.log('Login - After commits, localStorage userData:', localStorage.getItem(USER_DATA_KEY));
        
        
        // Store username
        localStorage.setItem(USERNAME_KEY, credentials.username);
        
        // Redirect to games page
        await router.push('/partidas');
        
        // Dispatch success notification
        dispatch('notifications/showSuccess', 'Login realizado com sucesso!', { root: true });
        
        return response;
      } catch (error) {
        let errorMessage = 'Erro ao fazer login';
        
        // Check if error message is in the error object
        if (error.message) {
          errorMessage = error.message;
        }
        
        commit('SET_ERROR', errorMessage);
        throw new Error(errorMessage);
      } finally {
        commit('SET_LOADING', false);
      }
    },
    
    async logout({ commit, dispatch }) {
      try {
        // Call logout endpoint if available
        await apiService.post('/logout/').catch(() => {});
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        // Clear auth state
        commit('CLEAR_AUTH');
        
        // Redirect to login
        await router.push('/login');
        
        // Show notification
        dispatch('notifications/showInfo', 'Você foi desconectado', { root: true });
      }
    },
    
    async checkAuth({ commit, dispatch }) {
      const token = localStorage.getItem(TOKEN_KEY);
      
      if (!token) {
        commit('SET_AUTHENTICATED', false);
        return false;
      }
      
      // Load user data from localStorage if available
      const userData = localStorage.getItem(USER_DATA_KEY);
      if (userData) {
        try {
          const user = JSON.parse(userData);
          commit('SET_USER', user);
          commit('SET_TOKEN', token);
          commit('SET_AUTHENTICATED', true);
          
          // Verify token with API in background (don't await)
          apiService.get('/verify-token/').then(response => {
            console.log('checkAuth - verify-token response:', response);
            if (!response || !response.valid) {
              // Token is invalid, logout
              dispatch('logout');
            } else if (response.user) {
              // Update user data if provided
              console.log('checkAuth - updating user from verify-token:', response.user);
              commit('SET_USER', response.user);
            }
          }).catch((error) => {
            console.log('checkAuth - verify-token error (ignored):', error);
            // Ignore errors - user can continue using cached data
          });
          
          return true;
        } catch (e) {
          // Invalid user data, proceed with API check
        }
      }
      
      try {
        // Verify token with API (if endpoint exists)
        const response = await apiService.get('/verify-token/');
        console.log('checkAuth - verify-token response:', response);
        
        // apiService returns data directly, not response.data
        if (response && response.valid) {
          commit('SET_TOKEN', token);
          commit('SET_USER', response.user);
          commit('SET_AUTHENTICATED', true);
          console.log('checkAuth - user set from verify-token:', response.user);
          return true;
        } else {
          dispatch('logout');
          return false;
        }
      } catch (error) {
        console.log('checkAuth - verify-token error:', error);
        // If error but we have token and valid user data, allow access
        const storedUser = localStorage.getItem(USER_DATA_KEY);
        if (token && storedUser) {
          try {
            const user = JSON.parse(storedUser);
            if (user && user.username) {
              console.log('checkAuth - using cached user data:', user);
              commit('SET_USER', user);
              commit('SET_TOKEN', token);
              commit('SET_AUTHENTICATED', true);
              return true;
            }
          } catch (e) {
            console.log('checkAuth - error parsing cached user data:', e);
            // Invalid user data
          }
        }
        // Otherwise logout
        console.log('checkAuth - logging out due to error and no valid cached data');
        dispatch('logout');
        return false;
      }
    },
    
    async refreshToken({ commit, dispatch }) {
      try {
        const response = await apiService.post('/refresh-token/');
        // apiService returns data directly
        const { token } = response;
        
        if (token) {
          commit('SET_TOKEN', token);
          return token;
        } else {
          throw new Error('No token received');
        }
      } catch (error) {
        dispatch('logout');
        throw error;
      }
    },
    
    setUser({ commit }, user) {
      commit('SET_USER', user);
    },
  },
  
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    currentUser: state => state.user,
    username: state => state.user?.username || localStorage.getItem(USERNAME_KEY),
    token: state => state.token,
    botPermissions: state => state.botPermissions,
    isAdmin: state => {
      // Admin is now based on is_superuser from backend
      console.log('Auth getter isAdmin - state.user:', state.user);
      console.log('Auth getter isAdmin - is_superuser:', state.user?.is_superuser);
      const isAdmin = state.user?.is_superuser === true;
      console.log('Auth getter isAdmin - returning:', isAdmin);
      return isAdmin;
    },
    hasPermission: state => botId => {
      return state.botPermissions.some(permission => permission.id === botId);
    },
    isLoading: state => state.isLoading,
    error: state => state.error,
  },
};